import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Bell, X, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SavedSearchForm = ({ categories, currentSearch, currentCategory }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [query, setQuery] = useState(currentSearch || '');
  const [category, setCategory] = useState(currentCategory || '');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim() || query.trim().length < 2) {
      setError(t('savedSearch.errQuery', 'Search term must be at least 2 characters'));
      return;
    }
    if (!email.trim() || !email.includes('@')) {
      setError(t('savedSearch.errEmail', 'Please enter a valid email'));
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/api/classifieds/saved-searches`, {
        searchQuery: query.trim(),
        category: category || null,
        email: email.trim(),
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('savedSearch.errSave', 'Failed to save search alert'));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4">
        <div className="flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-emerald-800" data-testid="saved-search-success">{t('savedSearch.saved', 'Alert saved!')}</p>
            <p className="text-xs text-emerald-600 mt-0.5">
              {t('savedSearch.savedDesc', "We'll email you when new items matching your search are listed.")}
            </p>
          </div>
          <button onClick={() => { setSuccess(false); setOpen(false); setQuery(''); setEmail(''); }} className="ml-auto text-emerald-400 hover:text-emerald-600">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        onClick={() => { setOpen(true); setQuery(currentSearch || ''); setCategory(currentCategory || ''); }}
        className="flex items-center gap-2 px-4 py-2.5 bg-white border border-emerald-200 rounded-xl text-sm text-emerald-700 hover:bg-emerald-50 transition-colors mb-4 w-full sm:w-auto"
        data-testid="open-saved-search-btn"
      >
        <Bell className="h-4 w-4" />
        {t('savedSearch.setAlert', 'Set a search alert')}
      </button>
    );
  }

  return (
    <div className="bg-white border border-emerald-200 rounded-xl p-4 mb-4 shadow-sm" data-testid="saved-search-form">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Bell className="h-4 w-4 text-emerald-600" />
          {t('savedSearch.title', 'Search Alert')}
        </h3>
        <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">
          <X className="h-4 w-4" />
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-3">{t('savedSearch.desc', "Get notified when new items matching your search are listed.")}</p>
      <form onSubmit={handleSubmit} className="space-y-2.5">
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setError(''); }}
          placeholder={t('savedSearch.queryPlaceholder', 'e.g. sofa, bicycle, iPhone...')}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
          data-testid="saved-search-query-input"
        />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
          data-testid="saved-search-category-select"
        >
          <option value="">{t('savedSearch.anyCategory', 'Any category')}</option>
          {(categories || []).map((cat) => (
            <option key={cat} value={cat}>{t(`classifieds.cat.${cat}`, cat)}</option>
          ))}
        </select>
        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setError(''); }}
          placeholder={t('savedSearch.emailPlaceholder', 'Your email for notifications')}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
          data-testid="saved-search-email-input"
        />
        {error && <p className="text-xs text-red-600" data-testid="saved-search-error">{error}</p>}
        <button
          type="submit"
          disabled={submitting}
          className="w-full py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
          data-testid="saved-search-submit-btn"
        >
          {submitting ? t('savedSearch.saving', 'Saving...') : t('savedSearch.save', 'Save Alert')}
        </button>
      </form>
    </div>
  );
};

export default SavedSearchForm;
